/**
 * Deterministic id minting.
 *
 * Ids come from a counter stored in the world (`WorldState.nextId`), so the
 * same seed and the same actions produce the same ids, and a pruned
 * impression's id is never handed out again. No module-level state.
 */

import type {
  Actor,
  AppointmentId,
  EntityRef,
  EventId,
  FactId,
  GrievanceId,
  ImpressionId,
  TextureId,
  WorldState,
} from './types/index.js';

export interface Minted<Id> {
  readonly id: Id;
  readonly state: WorldState;
}

function mint<Id extends string>(state: WorldState, prefix: string): Minted<Id> {
  const id = `${prefix}_${state.nextId}` as unknown as Id;
  return { id, state: { ...state, nextId: state.nextId + 1 } };
}

export const mintEventId = (s: WorldState): Minted<EventId> => mint(s, 'ev');
export const mintFactId = (s: WorldState): Minted<FactId> => mint(s, 'fact');
export const mintImpressionId = (s: WorldState): Minted<ImpressionId> => mint(s, 'imp');
export const mintTextureId = (s: WorldState): Minted<TextureId> => mint(s, 'tex');
export const mintAppointmentId = (s: WorldState): Minted<AppointmentId> => mint(s, 'appt');
export const mintGrievanceId = (s: WorldState): Minted<GrievanceId> => mint(s, 'grv');

/**
 * The canonical tag for an entity, used by impressions and retrieval (§19:
 * "array filters over tags"). One spelling everywhere, so a topic and a
 * memory can only ever match by string equality.
 */
export function entityTag(ref: EntityRef): string {
  return ref.kind === 'player' ? 'player' : `${ref.kind}:${ref.id}`;
}

export function actorTag(actor: Actor): string {
  return actor === 'player' ? 'player' : `npc:${actor}`;
}

export function actorRef(actor: Actor): EntityRef {
  return actor === 'player' ? { kind: 'player' } : { kind: 'npc', id: actor };
}

export function sameActor(a: Actor, b: Actor): boolean {
  return a === b;
}
